<script>
import axios from '@/utils/axios';
import { projectUtils } from '@/utils/project';
import { VueDraggableNext } from "vue-draggable-next";

export default {
 inject: ['setLoading'],
 components: {
  draggable: VueDraggableNext,
 },
 data() {
  return {
   errors: {}, 
   title: "Tasks",
   text: "Workspace",
   taskGroups: [],
   loadingStatus: {},
   search: '',
   priority: '',
   client_id: '',
   created_date: '',
   due_date: '',
   selectedStatus: '',
   sortBy: 'created_date',
   projectUtils,
   statusConfig: {
    'To Do': {
     dotBgClass: 'bg-transparent-purple',
     dotClass: 'bg-purple'
    },
    'In Progress': {
     dotBgClass: 'bg-soft-skyblue',
     dotClass: 'bg-skyblue'
    },
    'Review': {
     dotBgClass: 'bg-soft-warning',
     dotClass: 'bg-warning'
    },
    'Completed': {
     dotBgClass: 'bg-soft-success',
     dotClass: 'bg-success'
    },
    'Cancelled': {
     dotBgClass: 'bg-soft-pink',
     dotClass: 'bg-pink'
    }
   }
  };
 },

 computed: {
  text1() {
   const total = this.taskGroups.reduce((sum, group) => sum + group.total_count, 0);
   return `Tasks (${total})`;
  },
  
  totalTasks() {
   return this.taskGroups.reduce((sum, group) => sum + group.total_count, 0);
  },
  
  pendingTasks() {
   const toDoGroup = this.taskGroups.find(g => g.status === 'To Do');
   const inProgressGroup = this.taskGroups.find(g => g.status === 'In Progress');
   return (toDoGroup?.total_count || 0) + (inProgressGroup?.total_count || 0);
  },
  
  completedTasks() {
   const completed = this.taskGroups.find(g => g.status === 'Completed');
   return completed ? completed.total_count : 0;
  }
 },

 mounted() {
  this.fetchTasks();  
 },
 
 methods: {
  async fetchTasks() {
   this.errors = {}; 
   this.setLoading(true);
   
   try {
    const params = {
     search: this.search,
     priority: this.priority,
     client_id: this.client_id,
     created_date: this.created_date,
     due_date: this.due_date,
     status: this.selectedStatus,
     sort_by: this.sortBy
    };
    
    Object.keys(params).forEach(key => {
     if (!params[key]) delete params[key];
    });
    
    const response = await axios.get('/projects/tasks/my-tasks', { params });
    
    // Access the correct path: response.data.data
    this.taskGroups = response.data.data || [];
    
    // Initialize loading states - Vue 3 way
    this.taskGroups.forEach(group => {
     this.loadingStatus[group.status] = false;
    });
    
   } catch(error) {
    this.taskGroups = [];
    this.$swalToast(error.response?.data?.message || error.message, 'error');
   } finally {
    this.setLoading(false);
   } 
  },


  async loadMoreTasks(status) {
   const statusGroup = this.taskGroups.find(g => g.status === status);
   
   if (!statusGroup || !statusGroup.has_more || this.loadingStatus[status]) {
    return;
   }
   
   this.loadingStatus[status] = true;
   
   try {
    const params = {
     load_more: true,
     status: status,
     offset: statusGroup.current_count,
     search: this.search,
     priority: this.priority,
     client_id: this.client_id,
     created_date: this.created_date,
     due_date: this.due_date
    };
    
    Object.keys(params).forEach(key => {
     if (!params[key] && key !== 'load_more') delete params[key];
    });
    
    const response = await axios.get('/projects/tasks/my-tasks', { params });
    
    // Append new tasks
    statusGroup.tasks.push(...response.data.data.tasks);
    statusGroup.current_count = statusGroup.tasks.length;
    statusGroup.has_more = response.data.data.has_more;
    
   } catch(error) {
    this.$swalToast(error.response?.data?.message || error.message, 'error');
   } finally {
    this.loadingStatus[status] = false; // Direct assignment in Vue 3
   }
  },

  async saveTaskStatus(task_id, status){
    this.setLoading(true);
    try{
     await projectUtils.saveProjectTaskStatus(task_id, status);
     this.$swalToast('Task status updated successfully');
     this.$emit('updated');
    }catch(error){
     this.$swalToast(error.message, 'error');
    }finally{
     this.setLoading(false);
    }
   },

  filterByPriority(priority) {
   this.priority = priority === 'All' ? '' : priority;
   this.fetchTasks();
  },

  filterByStatus(status) {
   this.selectedStatus = status;
   this.fetchTasks();
  },

  clearFilter() {
   this.priority = '';
   this.search = '';
   this.selectedStatus = '';
   this.client_id = '';
   this.created_date = '';
   this.due_date = '';
   this.sortBy = 'created_date';
   this.fetchTasks();
  },
  
  applyFilters() {
   this.fetchTasks();
  },

  onDragEnd(evt) {
   if(!evt.to || !evt.from){
    // Drag ended outside droppable container or invalid event
    return;
   }

   const newStatusElement = evt.to.closest('.project-status > div');
   const oldStatusElement = evt.from.closest('.project-status > div');
   if(!newStatusElement || !oldStatusElement){
    // Could not find the parent container, exit safely
    return;
   }

   const newStatusIndex = Array.from(document.querySelectorAll('.project-status > div')).indexOf(newStatusElement);
   const oldStatusIndex = Array.from(document.querySelectorAll('.project-status > div')).indexOf(oldStatusElement);

   if(newStatusIndex === -1 || oldStatusIndex === -1 || newStatusIndex === oldStatusIndex){
    // No status change or invalid indices
    return;
   }

   const movedTask = this.taskGroups[newStatusIndex]?.tasks[evt.newIndex];
   if(!movedTask){
    console.error('Task not found after drag');
    return;
   }

   const newStatus = this.taskGroups[newStatusIndex].status;
   const taskId = movedTask.id;

   this.saveTaskStatus(taskId, newStatus);
  },

  getPriorityBadgeClass(priority) {
   return {
    'High': 'badge bg-danger',
    'Medium': 'badge bg-warning',
    'Low': 'badge bg-success'
   }[priority] || 'badge bg-secondary';
  },

  getProgressClass(status) {
   return {
    'To Do': 'bg-secondary',
    'In Progress': 'bg-info',
    'Review': 'bg-warning',
    'Completed': 'bg-success',
    'Cancelled': 'bg-danger'
   }[status] || 'bg-secondary';
  },

  getProgressPercentage(task) {
   return task.progress || '0%';
  }
 }
};
</script>

<template>
<layout-header></layout-header>
<layout-sidebar></layout-sidebar>

<!-- Page Wrapper -->
<div class="page-wrapper">
 <div class="content">
  <!-- Breadcrumb -->
  <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
   <breadcrumb :title="title" :text="text" :text1="text1" />
   <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
    <!-- <a href="javascript:void(0);" class="btn btn-primary d-inline-flex align-items-center" data-bs-toggle="modal" data-bs-target="#add_board">
     <i class="ti ti-circle-plus me-1"></i>Add Board
    </a> -->
   </div>
  </div>
  
  <div class="card">
   <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
    <h4>My Tasks</h4>
    <div class="d-flex align-items-center flex-wrap row-gap-3">
     <div class="d-flex align-items-center me-3">
      <p class="mb-0 me-3 pe-3 border-end fs-14">
       Total Task : <span class="text-dark">{{ totalTasks }}</span>
      </p>
      <p class="mb-0 me-3 pe-3 border-end fs-14">
       Pending : <span class="text-dark">{{ pendingTasks }}</span>
      </p>
      <p class="mb-0 fs-14">
       Completed : <span class="text-dark">{{ completedTasks }}</span>
      </p>
     </div>
     <div class="input-icon-start position-relative">
      <span class="input-icon-addon">
       <i class="ti ti-search"></i>
      </span>
      <input type="text" class="form-control" placeholder="Search Tasks" v-model="search" @input="applyFilters"/>
     </div>
    </div>
   </div>
   
   <div class="card-body">
    <div class="row">
     <!-- Priority Filter -->
     <div class="col-lg-4">
      <div class="d-flex align-items-center flex-wrap row-gap-3 mb-3">
       <h6 class="me-2">Priority</h6>
       <ul class="nav nav-pills border d-inline-flex p-1 rounded bg-light todo-tabs" role="tablist">
        <li class="nav-item" role="presentation">
         <button class="nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto" :class="{ active: priority === '' }" @click="filterByPriority('All')" type="button">
          All
         </button>
        </li>
        <li class="nav-item" role="presentation">
         <button class="nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto" :class="{ active: priority === 'High' }" @click="filterByPriority('High')" type="button">
          High
         </button>
        </li>
        <li class="nav-item" role="presentation">
         <button class="nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto" :class="{ active: priority === 'Medium' }" @click="filterByPriority('Medium')" type="button">
          Medium
         </button>
        </li>
        <li class="nav-item" role="presentation">
         <button class="nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto" :class="{ active: priority === 'Low' }" @click="filterByPriority('Low')" type="button">
          Low
         </button>
        </li>
       </ul>
      </div>
     </div>
     
     <!-- Other Filters -->
     <div class="col-lg-8">
      <div class="d-flex align-items-center justify-content-lg-end flex-wrap row-gap-3 mb-3">
       <!-- Status Filter -->
       <div class="dropdown me-2">
        <a href="javascript:void(0);" class="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
         {{ selectedStatus || 'Select Status' }}
        </a>
        <ul class="dropdown-menu dropdown-menu-end p-3">
         <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="filterByStatus('')">All</a></li>
         <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="filterByStatus('To Do')">To Do</a></li>
         <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="filterByStatus('In Progress')">In Progress</a></li>
         <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="filterByStatus('Review')">Review</a></li>
         <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="filterByStatus('Completed')">Completed</a></li>
         <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="filterByStatus('Cancelled')">Cancelled</a></li>
        </ul>
       </div>
       
       <!-- Created Date -->
       <!-- <div class="input-icon w-120 position-relative me-2">
        <span class="input-icon-addon">
         <i class="ti ti-calendar"></i>
        </span>
        <date-picker v-model="created_date" class="form-control datetimepicker" placeholder="Created Date" :editable="true" :clearable="true" @change="applyFilters" />
       </div> -->
       
       <!-- Due Date -->
       <!-- <div class="input-icon w-120 position-relative me-2">
        <span class="input-icon-addon">
         <i class="ti ti-calendar"></i>
        </span>
        <date-picker v-model="due_date" class="form-control datetimepicker" placeholder="Due Date" :editable="true" :clearable="true" @change="applyFilters"/>
       </div> -->
       
       <!-- Sort By -->
       <div class="d-flex align-items-center border rounded p-2 me-2">
        <span class="d-inline-flex me-2">Sort By : </span>
        <div class="dropdown">
         <a href="javascript:void(0);" class="dropdown-toggle btn btn-white d-inline-flex align-items-center border-0 bg-transparent p-0 text-dark" data-bs-toggle="dropdown">
          {{ sortBy === 'created_date' ? 'Created Date' : sortBy === 'start_date' ? 'Start Date' : 'Due Date' }}
         </a>
         <ul class="dropdown-menu dropdown-menu-end p-3">
          <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="sortBy = 'created_date'; applyFilters()">Created Date</a></li>
          <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="sortBy = 'start_date'; applyFilters()">Start Date</a></li>
          <li><a href="javascript:void(0);" class="dropdown-item rounded-1" @click="sortBy = 'due_date'; applyFilters()">Due Date</a></li>
         </ul>
        </div>
       </div>
       
       <!-- Clear Filters -->
       <button class="btn btn-light" @click="clearFilter">
        <i class="ti ti-x"></i> Clear
       </button>
      </div>
     </div>
    </div>
    
    <!-- Kanban Board -->
    <div class="d-flex align-items-start overflow-auto project-status pb-4">
     <div class="p-3 rounded bg-transparent-secondary w-100 me-3" v-for="group in taskGroups" :key="group.status" style="min-width: 320px;">
      <!-- Status Header -->
      <div class="bg-white p-2 rounded mb-2">
       <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
         <span class="p-1 d-flex rounded-circle me-2" :class="statusConfig[group.status]?.dotBgClass">
          <span class="rounded-circle d-block p-1" :class="statusConfig[group.status]?.dotClass"></span>
         </span>
         <h5 class="me-2">{{ group.status }}</h5>
         <span class="badge bg-light rounded-pill">{{ group.total_count }}</span>
        </div>
       </div>
      </div>
      
      <!-- Draggable Task Cards -->
      <div class="kanban-drag-wrap">
       <draggable :list="group.tasks" :group="{ name: 'kanban', pull: true, put: true }" @end="onDragEnd">
        <div v-for="task in group.tasks" :key="task.id">
         <div class="card kanban-card mb-2">
          <div class="card-body">
           <!-- Task Header -->
           <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center">
             <a :href="`/workspace/project-details/${task?.project?.id}`" target="_blank"><span class="badge bg-outline-dark me-2">{{ task.project?.name }}</span></a>
             <span class="badge-xs d-flex align-items-center justify-content-center" :class="getPriorityBadgeClass(task.priority)">
              <i class="fas fa-circle fs-6 me-1"></i>{{ task.priority }}
             </span>
            </div>
            <!-- <div class="dropdown">
             <a href="javascript:void(0);" class="d-inline-flex align-items-center" data-bs-toggle="dropdown">
              <i class="ti ti-dots-vertical"></i>
             </a>
             <ul class="dropdown-menu dropdown-menu-end p-3">
              <li>
               <a href="javascript:void(0);" class="dropdown-item rounded-1" data-bs-toggle="modal" data-bs-target="#edit_task">
                <i class="ti ti-edit me-2"></i>Edit
               </a>
              </li>
              <li>
               <a href="javascript:void(0);" class="dropdown-item rounded-1" data-bs-toggle="modal" data-bs-target="#delete_modal">
                <i class="ti ti-trash me-2"></i>Delete
               </a>
              </li>
             </ul>
            </div> -->
           </div>
           
           <!-- Task Title -->
           <div class="mb-2">
            <h6 class="d-flex align-items-center"><a href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#project_task_view_modal" @click="$refs.projectModal.task(task?.project_id, task);">{{ task.name }}</a></h6>
            <p v-if="task.description" class="text-muted small mb-0">{{ task.description }}</p>
           </div>
           
           <!-- Progress Bar -->
           <!-- <div class="d-flex align-items-center mb-2">
             <div class="progress progress-sm flex-fill" role="progressbar">
             <div class="progress-bar" :class="getProgressClass(group.status)" :style="{ width: getProgressPercentage(task) }"></div>
            </div> 
            <span class="d-block ms-2 text-gray-9 fw-medium">{{ getProgressPercentage(task) }}</span>
           </div> -->
           
           <!-- Due Date -->
           <p class="fw-medium mb-0">
            Due on : <span class="text-gray-9">{{ task.due_date ? $formatDate(task.due_date) : 'N/A' }}</span>
           </p>
           
           <!-- Footer with Assignees -->
           <div class="d-flex align-items-center justify-content-between border-top pt-2 mt-2">
            <div class="avatar-list-stacked avatar-group-sm me-3">
             <span v-if="task.assignee?.image" class="avatar avatar-rounded">
              <img class="border border-white" :src="task.assignee.image" alt="img" />
             </span>
             <span v-if="task.creator?.image" class="avatar avatar-rounded">
              <img class="border border-white" :src="task.creator.image" alt="img" />
             </span>
            </div>
            <div class="d-flex align-items-center">
             <a href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#project_task_view_modal" @click="$refs.projectModal.task(task?.project_id, task);" class="d-flex align-items-center text-dark me-2">
              <i class="ti ti-message-circle text-gray me-1"></i>{{ task?.comments.length || 0 }}
             </a>
            </div>
           </div>
          </div>
         </div>
        </div>
       </draggable>
      </div>
      
      <!-- Load More Button -->
      <div class="pt-2">
       <button v-if="group.has_more" class="btn btn-white border border-dashed d-flex align-items-center justify-content-center w-100 mb-2" :disabled="loadingStatus[group.status]" @click="loadMoreTasks(group.status)">
        <i v-if="!loadingStatus[group.status]" class="ti ti-reload me-2"></i>
        <i v-else class="ti ti-loader spin me-2"></i>
        {{ loadingStatus[group.status] ? 'Loading...' : `Load More (${group.total_count - group.current_count})` }}
       </button>
      </div>
     </div>
    </div>
   </div>
  </div>
 </div>
 <app-footer></app-footer>
</div>
<!-- /Page Wrapper -->
<project-modal ref="projectModal" @updated="fetchTasks"></project-modal>
</template>

<style scoped>
.kanban-drag-wrap {
 min-height: 100px;
}
.kanban-card {
 cursor: move;
}
.project-status {
 display: flex;
 gap: 16px;
}
.bg-transparent-secondary {
 background-color: #f8f9fa;
}
</style>
